// src/components/TeamAttendanceLeave.jsx
import React, { useEffect, useState } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  getTeamAttendance,
  getTeamHoursSummary,
  getAttendanceHistory,
  getCorrections,
  approveCorrection,
  rejectCorrection,
} from "../services/AttendanceService";
import { getAttendanceEligibleUsers } from "../services/LeaveService";
import AdminLeaveDashboard from "./AdminLeaveDashboard";
import AdminSalaryManagement from "./AdminSalaryManagement";
import LeaveCalendar from "./LeaveCalendar";
import { toLocalDateStr } from "../utils/date";
import { hasPermission } from "../utils/auth";
import { menuPortalTarget, menuPosition, themedSelectStyles } from "../utils/reactSelectTheme";
import ToastContainer, { useToast } from "./common/Toast";
import "../css/theme.css";
import "../css/components.css";
import "./TeamAttendanceLeave.css";

const LOCATION_BADGE = {
  OFFICE: { label: "Office", className: "badge-office" },
  REMOTE: { label: "Remote", className: "badge-remote" },
  UNKNOWN: { label: "Unknown", className: "badge-unknown" },
};

const STATUS_LABEL = {
  PRESENT: "Present",
  ABSENT: "Absent",
  ON_LEAVE: "On Leave",
  WFH: "WFH",
  HALF_DAY: "Half Day",
};

// Matches AdminSalaryManagement.jsx's own MONTH_NAMES - this codebase
// duplicates this small array per-file rather than sharing one.
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function LocationBadge({ type }) {
  if (!type) return <span className="location-badge badge-unknown">--</span>;
  const badge = LOCATION_BADGE[type] || LOCATION_BADGE.UNKNOWN;
  return <span className={`location-badge ${badge.className}`}>{badge.label}</span>;
}

function formatTime(value) {
  if (!value) return "--";
  return new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDisplayDate(isoDate) {
  if (!isoDate) return "--";
  const [year, month, day] = isoDate.split("-");
  return `${day}/${month}/${year}`;
}

// Hours actually worked that day, or null if there's nothing to compute
// from - either no record, or an incomplete one (checked in but never
// checked out, including "still checked in today"). Matches LeaveCalendar.
// jsx's own hoursForRecord - duplicated per-file, same convention as every
// other small helper in this app.
function hoursForRecord(record) {
  if (!record || !record.checkInTime || !record.checkOutTime) return null;
  const hours = (new Date(record.checkOutTime) - new Date(record.checkInTime)) / 3_600_000;
  return hours > 0 ? hours : null;
}

const TeamAttendanceLeave = () => {
  const { toasts, showToast, dismissToast } = useToast();
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  // Mirrors the backend hierarchy rule in AttendanceService.assertCanActOnCorrection:
  // nobody can approve their own request, and only SUPER_ADMIN can act on a
  // SUPER_USER's correction request.
  const canActOn = (correction) => {
    if (correction.userId === currentUser.userId) return false;
    if (correction.userRole === "SUPER_USER" && currentUser.role !== "SUPER_ADMIN") return false;
    return true;
  };

  const [date, setDate] = useState(new Date());
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingCorrections, setPendingCorrections] = useState([]);
  const [correctionsLoading, setCorrectionsLoading] = useState(false);
  const [attendanceUsers, setAttendanceUsers] = useState([]);
  // Per-row guard against double-click/slow-network double-submits on
  // Approve/Reject - a Set of correction-request ids currently being acted on.
  const [actingOnIds, setActingOnIds] = useState(new Set());
  const [selectedCalendarUserId, setSelectedCalendarUserId] = useState("");
  const [activeTab, setActiveTab] = useState("attendance");

  // "Working Hours" tab - independent of the single-day roster's own date
  // picker above, since this is a month-wide view.
  const today = new Date();
  const [hoursYear, setHoursYear] = useState(today.getFullYear());
  const [hoursMonth, setHoursMonth] = useState(today.getMonth() + 1);
  const [hoursSummary, setHoursSummary] = useState([]);
  const [hoursLoading, setHoursLoading] = useState(false);
  // Selecting an employee from the dropdown shows their full month, day by
  // day - reuses the same hoursYear/hoursMonth picker above rather than a
  // second one.
  const [selectedHoursUserId, setSelectedHoursUserId] = useState("");
  const [hoursUserRecords, setHoursUserRecords] = useState([]);
  const [hoursUserRecordsLoading, setHoursUserRecordsLoading] = useState(false);

  const loadTeamAttendance = async (targetDate) => {
    try {
      setLoading(true);
      const data = await getTeamAttendance(targetDate);
      setRecords(data || []);
    } catch (err) {
      console.error("Failed to load team attendance:", err);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingCorrections = async () => {
    try {
      setCorrectionsLoading(true);
      const data = await getCorrections("PENDING");
      setPendingCorrections(data || []);
    } catch (err) {
      console.error("Failed to load pending corrections:", err);
      setPendingCorrections([]);
    } finally {
      setCorrectionsLoading(false);
    }
  };

  useEffect(() => {
    loadTeamAttendance(toLocalDateStr(date));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    loadPendingCorrections();
  }, []);

  // Fetched for both roles now, not just SUPER_ADMIN - the Team Attendance
  // table below needs the full roster to show everyone, not just whoever
  // already has a record for the selected date.
  useEffect(() => {
    const loadAttendanceUsers = async () => {
      try {
        const data = await getAttendanceEligibleUsers();
        setAttendanceUsers(data || []);
      } catch (err) {
        console.error("Failed to load attendance-eligible users:", err);
        setAttendanceUsers([]);
      }
    };
    loadAttendanceUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadHoursSummary = async () => {
      try {
        setHoursLoading(true);
        const data = await getTeamHoursSummary(hoursYear, hoursMonth);
        setHoursSummary(data || []);
      } catch (err) {
        console.error("Failed to load team hours summary:", err);
        setHoursSummary([]);
      } finally {
        setHoursLoading(false);
      }
    };
    loadHoursSummary();
  }, [hoursYear, hoursMonth]);

  useEffect(() => {
    if (!selectedHoursUserId) {
      setHoursUserRecords([]);
      return;
    }
    const loadHoursUserRecords = async () => {
      try {
        setHoursUserRecordsLoading(true);
        const data = await getAttendanceHistory(selectedHoursUserId, hoursYear, hoursMonth);
        setHoursUserRecords(data || []);
      } catch (err) {
        console.error("Failed to load employee's month attendance:", err);
        setHoursUserRecords([]);
      } finally {
        setHoursUserRecordsLoading(false);
      }
    };
    loadHoursUserRecords();
  }, [selectedHoursUserId, hoursYear, hoursMonth]);

  const handleApproveCorrection = async (id) => {
    if (actingOnIds.has(id)) return;
    setActingOnIds((prev) => new Set(prev).add(id));
    try {
      await approveCorrection(id);
      await loadPendingCorrections();
      await loadTeamAttendance(date);
    } catch (err) {
      showToast(err.message || "Failed to approve.", "danger");
    } finally {
      setActingOnIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleRejectCorrection = async (id) => {
    if (actingOnIds.has(id)) return;
    setActingOnIds((prev) => new Set(prev).add(id));
    try {
      await rejectCorrection(id);
      await loadPendingCorrections();
    } catch (err) {
      showToast(err.message || "Failed to reject.", "danger");
    } finally {
      setActingOnIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const calendarUserOptions = attendanceUsers.map((u) => ({ value: u.userId, label: `${u.name} (${u.userId})` }));

  // Full roster (attendanceUsers) left-joined with today's records - a user
  // with no AttendanceRecord yet (hasn't checked in, and the nightly sweep
  // hasn't run) still gets a row instead of silently disappearing from the
  // table, same idea as LeaveCalendar's "no record for this date" gap-fill.
  const attendanceByUserId = new Map(records.map((r) => [r.userId, r]));
  const rosterRows = attendanceUsers.map((u) => ({
    userId: u.userId,
    userName: u.name || u.userId,
    record: attendanceByUserId.get(u.userId) || null,
  }));

  // Same left-join idea as rosterRows above, but against the month-wide
  // hours summary instead of a single day's records - an employee with no
  // AttendanceRecord that month still gets a row ("No data") instead of
  // silently disappearing from the table.
  const hoursByUserId = new Map(hoursSummary.map((h) => [h.userId, h]));
  const hoursRows = attendanceUsers.map((u) => ({
    userId: u.userId,
    userName: u.name || u.userId,
    summary: hoursByUserId.get(u.userId) || null,
  }));

  const hoursMonthOptions = MONTH_NAMES.map((m, idx) => ({ value: idx + 1, label: m }));
  const hoursYearOptions = [today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1]
    .map((y) => ({ value: y, label: String(y) }));

  const canSeeWorkingHoursTab = currentUser.role === "SUPER_ADMIN";

  return (
    <div className="team-attendance-leave">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      <div className="vt-page-header">
        <h2>Workforce</h2>
      </div>

      <div className="vt-tabs">
        <button
          type="button"
          className={`vt-tab ${activeTab === "attendance" ? "active" : ""}`}
          onClick={() => setActiveTab("attendance")}
        >
          Attendance
        </button>
        <button
          type="button"
          className={`vt-tab ${activeTab === "leave" ? "active" : ""}`}
          onClick={() => setActiveTab("leave")}
        >
          Leave
        </button>
        {/* manage_salary is narrower than this page's own manage_leaves gate
            (SUPER_ADMIN and SUPER_USER have both, other roles have neither)
            - hidden entirely for anyone without it, rather than shown and
            blocked. */}
        {hasPermission("manage_salary") && (
          <button
            type="button"
            className={`vt-tab ${activeTab === "salary" ? "active" : ""}`}
            onClick={() => setActiveTab("salary")}
          >
            Salary
          </button>
        )}
        {/* Same audience as the Employee Attendance Calendar below -
            management-level reporting, not a day-to-day attendance task. */}
        {canSeeWorkingHoursTab && (
          <button
            type="button"
            className={`vt-tab ${activeTab === "hours" ? "active" : ""}`}
            onClick={() => setActiveTab("hours")}
          >
            Working Hours
          </button>
        )}
      </div>

      {activeTab === "attendance" && (
        <>
          <div className="attendance-roster-card">
            <div className="attendance-roster-header">
              <h2 className="section-title">Team Attendance</h2>
              <DatePicker
                selected={date}
                onChange={(d) => d && setDate(d)}
                dateFormat="dd/MM/yyyy"
                className="date-picker"
                portalId="team-attendance-datepicker-portal"
              />
            </div>

            {loading && <div className="loading-overlay">Loading...</div>}

            <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Check-In</th>
                  <th>Check-Out</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rosterRows.length > 0 ? (
                  rosterRows.map((row) => (
                    <tr key={row.userId}>
                      <td>{row.userName}</td>
                      {row.record ? (
                        <>
                          <td>
                            {formatTime(row.record.checkInTime)} <LocationBadge type={row.record.checkInLocationType} />
                          </td>
                          <td>
                            {formatTime(row.record.checkOutTime)} <LocationBadge type={row.record.checkOutLocationType} />
                          </td>
                          <td>{STATUS_LABEL[row.record.status] || row.record.status || "--"}</td>
                        </>
                      ) : (
                        <>
                          <td>--</td>
                          <td>--</td>
                          <td><span className="vt-badge vt-badge-warning">Not Checked In</span></td>
                        </>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: "center" }}>
                      No employees found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          </div>

          {(currentUser.role === "SUPER_ADMIN" || currentUser.role === "SUPER_USER") && (
            <div className="attendance-roster-card">
              <div className="attendance-roster-header">
                <h2 className="section-title">Employee Attendance Calendar</h2>
                <Select
                  options={calendarUserOptions}
                  value={calendarUserOptions.find((o) => o.value === selectedCalendarUserId) || null}
                  onChange={(selected) => setSelectedCalendarUserId(selected ? selected.value : "")}
                  placeholder="Select Employee"
                  isClearable
                  isSearchable={false}
                  classNamePrefix="react-select"
                  className="react-select-container filter-select"
                  menuPortalTarget={menuPortalTarget}
                  menuPosition={menuPosition}
                  styles={themedSelectStyles()}
                />
              </div>
              {selectedCalendarUserId && (
                <LeaveCalendar userId={selectedCalendarUserId} adminView={true} showAttendance={true} />
              )}
            </div>
          )}

          <div className="attendance-roster-card">
            <div className="attendance-roster-header">
              <h2 className="section-title">Pending Attendance Corrections</h2>
            </div>
            {correctionsLoading && <div className="loading-overlay">Loading...</div>}
            <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Date</th>
                  <th>Requested Status</th>
                  <th>Reason</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingCorrections.length > 0 ? (
                  pendingCorrections.map((c) => (
                    <tr key={c.id}>
                      <td>{c.userName || c.userId}</td>
                      <td>{c.date}</td>
                      <td>{STATUS_LABEL[c.requestedStatus] || c.requestedStatus}</td>
                      <td>{c.reason}</td>
                      <td>
                        {canActOn(c) ? (
                          <>
                            <button className="btn-approve" onClick={() => handleApproveCorrection(c.id)} disabled={actingOnIds.has(c.id)}>
                              {actingOnIds.has(c.id) ? "..." : "Approve"}
                            </button>
                            <button className="btn-reject" onClick={() => handleRejectCorrection(c.id)} disabled={actingOnIds.has(c.id)}>
                              {actingOnIds.has(c.id) ? "..." : "Reject"}
                            </button>
                          </>
                        ) : (
                          <span className="leave-action-hint">
                            {c.userRole === "SUPER_USER" ? "Requires Super Admin" : "Not actionable"}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={{ textAlign: "center" }}>
                      No pending correction requests
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          </div>
        </>
      )}

      {activeTab === "hours" && canSeeWorkingHoursTab && (
        <div className="attendance-roster-card">
          <div className="attendance-roster-header">
            <h2 className="section-title">Average Working Hours</h2>
            <div className="month-picker">
              <Select
                options={hoursMonthOptions}
                value={hoursMonthOptions.find((o) => o.value === hoursMonth)}
                onChange={(selected) => setHoursMonth(selected.value)}
                isSearchable={false}
                classNamePrefix="react-select"
                className="react-select-container filter-select"
                menuPortalTarget={menuPortalTarget}
                menuPosition={menuPosition}
                styles={themedSelectStyles()}
              />
              <Select
                options={hoursYearOptions}
                value={hoursYearOptions.find((o) => o.value === hoursYear)}
                onChange={(selected) => setHoursYear(selected.value)}
                isSearchable={false}
                classNamePrefix="react-select"
                className="react-select-container filter-select"
                menuPortalTarget={menuPortalTarget}
                menuPosition={menuPosition}
                styles={themedSelectStyles()}
              />
            </div>
          </div>
          {hoursLoading && <div className="loading-overlay">Loading...</div>}
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Avg Daily Hours</th>
                  <th>Days Worked (Complete)</th>
                  <th>Incomplete Days</th>
                </tr>
              </thead>
              <tbody>
                {hoursRows.length > 0 ? (
                  hoursRows.map((row) => (
                    <tr key={row.userId}>
                      <td>{row.userName}</td>
                      <td>{row.summary?.avgDailyHours != null ? `${row.summary.avgDailyHours.toFixed(2)} hrs` : "No data"}</td>
                      <td>{row.summary?.completeDays ?? 0}</td>
                      <td>{row.summary?.incompleteDays ?? 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: "center" }}>
                      No employees found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Full month, day by day, for one selected employee - same
              hoursYear/hoursMonth picker above drives this too, no second
              one needed. */}
          <div className="attendance-roster-header" style={{ marginTop: "24px" }}>
            <h2 className="section-title">Employee's Monthly Hours</h2>
            <Select
              options={calendarUserOptions}
              value={calendarUserOptions.find((o) => o.value === selectedHoursUserId) || null}
              onChange={(selected) => setSelectedHoursUserId(selected ? selected.value : "")}
              placeholder="Select Employee"
              isClearable
              isSearchable={false}
              classNamePrefix="react-select"
              className="react-select-container filter-select"
              menuPortalTarget={menuPortalTarget}
              menuPosition={menuPosition}
              styles={themedSelectStyles()}
            />
          </div>
          {selectedHoursUserId && (
            <>
              {hoursUserRecordsLoading && <div className="loading-overlay">Loading...</div>}
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Check-In</th>
                      <th>Check-Out</th>
                      <th>Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hoursUserRecords.length > 0 ? (
                      [...hoursUserRecords]
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .map((r) => {
                          const incomplete = r.checkInTime && !r.checkOutTime;
                          const hours = hoursForRecord(r);
                          return (
                            <tr key={r.date}>
                              <td>{formatDisplayDate(r.date)}</td>
                              <td>{STATUS_LABEL[r.status] || r.status || "--"}</td>
                              <td>{formatTime(r.checkInTime)}</td>
                              <td>{formatTime(r.checkOutTime)}</td>
                              <td>
                                {incomplete ? "Incomplete" : hours != null ? `${hours.toFixed(1)}h` : "--"}
                              </td>
                            </tr>
                          );
                        })
                    ) : (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center" }}>
                          No attendance records for this month
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === "leave" && <AdminLeaveDashboard />}

      {activeTab === "salary" && hasPermission("manage_salary") && <AdminSalaryManagement />}
    </div>
  );
};

export default TeamAttendanceLeave;
